// What the Inbox list and its canvas show, derived from one Inbox answer (inbox-api.ts).
// Pure: no React, no requests, so every rule here is tested with plain values.
import type { InboxCopy } from '@/i18n/ns/inbox';
import type { WorkspaceIconName } from './icons';
import type { Inbox, InboxItem, InboxUpdate } from './inbox-api';
import { inboxKindExtension } from './inbox-extensions';
import { completeMarkdown, inlinePlainText, markdownPlainText } from './plain-text';

// Each kind's label (in the inbox copy namespace) and icon. Unknown kinds read as the Inbox.
const KIND_COPY: Record<string, keyof InboxCopy> = {
  sharing_review: 'kindSharingReview',
  skill_publish_review: 'kindSkillPublish',
  page_proposal: 'kindPage',
  agent_edit_access: 'kindEditAccess',
  shared_with_you: 'kindSharedWithYou',
  made_private: 'kindMadePrivate',
  agent_run: 'kindAgentRun',
  agent_published: 'kindAgentVersion',
  agent_edit_granted: 'kindEditGranted',
  agent_company_owned: 'kindAgentOwnership',
  agent_schedule_disabled: 'kindAgentSchedule',
  request_decided: 'kindYourRequest',
  follow_up_decided: 'kindFollowUp',
  follow_up_suggestion: 'kindFollowUp',
  result_edited: 'kindResultEdited',
  meeting_ready: 'kindMeetingReady',
};
const KIND_ICONS: Record<string, WorkspaceIconName> = {
  sharing_review: 'globe',
  skill_publish_review: 'skills',
  page_proposal: 'knowledge',
  meeting_ready: 'meetings',
  result_edited: 'results',
  agent_edit_access: 'agents',
  agent_run: 'agents',
  agent_published: 'agents',
  agent_edit_granted: 'agents',
  agent_company_owned: 'agents',
  agent_schedule_disabled: 'agents',
  shared_with_you: 'users',
  made_private: 'lock',
  follow_up_suggestion: 'chat',
  follow_up_decided: 'chat',
};
export const kindCopyKey = (kind: string): keyof InboxCopy => KIND_COPY[kind] ?? 'kindOther';
export const kindIcon = (kind: string): WorkspaceIconName =>
  inboxKindExtension(kind)?.icon ?? KIND_ICONS[kind] ?? 'inbox';

// The Inbox item id of a sharing request (inbox/service.mjs namespaces every provider's
// ids), and back.
const SHARING_ITEM = 'sharing:';
export const sharingItemId = (requestId: string) => `${SHARING_ITEM}${requestId}`;
export const sharingRequestId = (itemId: string | null | undefined) =>
  itemId?.startsWith(SHARING_ITEM) ? itemId.slice(SHARING_ITEM.length) : null;

/**
 * Updates that say the same thing about the same object (the runs of one agent, the
 * same kind and title) read as one row: the latest, with the earlier ones behind it.
 * `unread` holds every unread id in the group, so opening it reads them all.
 */
export interface UpdateGroup {
  latest: InboxUpdate;
  items: InboxUpdate[];
  unread: string[];
}
export function groupUpdates(updates: readonly InboxUpdate[]): UpdateGroup[] {
  const groups = new Map<string, UpdateGroup>();
  for (const update of updates) {
    const source = update.sourceKey || update.href || update.id;
    const key = `${update.kind}\u0000${source}\u0000${update.title}`;
    const group = groups.get(key);
    if (group) group.items.push(update);
    else groups.set(key, { latest: update, items: [update], unread: [] });
    if (!update.read) groups.get(key)!.unread.push(update.id);
  }
  return [...groups.values()];
}

export type InboxEntry =
  | { section: 'needs' | 'waiting'; item: InboxItem }
  | { section: 'update'; item: InboxUpdate; group: UpdateGroup }
  // A sharing request the lists do not hold: decided, or opened from Recent decisions.
  | { section: 'decision'; requestId: string };

/** The Inbox entry a canvas id names, or null when it has left the Inbox. */
export function findInboxEntry(inbox: Inbox | null, id: string): InboxEntry | null {
  if (inbox) {
    const needs = inbox.needsYou.find((item) => item.id === id);
    if (needs) return { section: 'needs', item: needs };
    const waiting = inbox.waiting.find((item) => item.id === id);
    if (waiting) return { section: 'waiting', item: waiting };
    for (const group of groupUpdates(inbox.updates)) {
      const item = group.items.find((update) => update.id === id);
      if (item) return { section: 'update', item, group };
    }
  }
  const requestId = sharingRequestId(id);
  return requestId ? { section: 'decision', requestId } : null;
}

/** The list row an open canvas belongs to: an earlier run selects its group's row. */
export function rowIdFor(inbox: Inbox | null, id: string | null | undefined) {
  if (!inbox || !id) return id ?? null;
  const group = groupUpdates(inbox.updates).find((each) =>
    each.items.some((update) => update.id === id),
  );
  return group ? group.latest.id : id;
}

/** A title as words: an item's title may be model text and carry markdown. */
export const itemTitle = (item: Pick<InboxItem, 'title'>) => inlinePlainText(item.title);

/** An update's body in one line for its row: its words, never its markdown. */
export function updateExcerpt(update: Pick<InboxUpdate, 'body'>) {
  return update.body ? markdownPlainText(completeMarkdown(update.body).text) : '';
}

/** The page's counts, as the list shows them: needs-you drives the badge; updates get dots. */
export function inboxCounts(inbox: Inbox | null) {
  return {
    needsYou: inbox?.counts.needsYou ?? 0,
    unreadRows: inbox
      ? groupUpdates(inbox.updates).filter((group) => group.unread.length).length
      : 0,
  };
}

const DAY = 86_400_000;
/** "5 min ago", "yesterday", "3 days ago" within a week; then "26 Sept" (the year only when it is not this year). */
export function whenLabel(value: string | null | undefined, locale: string, now = Date.now()) {
  const date = value ? new Date(value) : null;
  if (!date || !Number.isFinite(date.getTime())) return '';
  const elapsed = now - date.getTime();
  if (elapsed >= 0 && elapsed < 7 * DAY) {
    const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
    if (elapsed < 60_000) return relative.format(0, 'minute');
    if (elapsed < 3_600_000) return relative.format(-Math.floor(elapsed / 60_000), 'minute');
    if (elapsed < DAY) return relative.format(-Math.floor(elapsed / 3_600_000), 'hour');
    return relative.format(-Math.floor(elapsed / DAY), 'day');
  }
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

/** The absolute time for a canvas heading: "26 Sept 2026, 21:31". */
export function dateTimeLabel(value: string | null | undefined, locale: string) {
  const date = value ? new Date(value) : null;
  if (!date || !Number.isFinite(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

// Which of an item's server actions is its decision: the one that approves (by its
// route's last path segment), then the others as quiet alternatives.
export function orderedActions<T extends { path: string; body?: unknown }>(actions: readonly T[]) {
  const approves = (action: T) =>
    action.path.split('?')[0].endsWith('/approve') ||
    (typeof action.body === 'object' &&
      action.body !== null &&
      'grant' in action.body &&
      action.body.grant === true);
  return {
    primary: actions.find(approves) ?? null,
    others: actions.filter((action) => !approves(action)),
  };
}
